import React, { memo, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Tooltip as RechartsTooltip,
  Cell,
  Legend,
} from 'recharts';
import TimeRangeToggle from './TimeRangeToggle';
import { useEngagementInsights } from '../../hooks/useEngagementInsights';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';

const COLORS = ['#41C8C4', '#7C4DFF', '#FF7043', '#26C6DA', '#66BB6A', '#FFCA28'];

const DeviceClientBreakdown: React.FC = () => {
  const [timeRange, setTimeRange] = useState<EngagementTimeRange>('30d');
  const { data, isLoading, error, refetch, isFetching } = useEngagementInsights(timeRange);

  const deviceData = useMemo(() => data?.devices ?? [], [data]);
  const clientData = useMemo(() => data?.clients ?? [], [data]);

  return (
    <Card elevation={0} sx={{ height: '100%' }}>
      <CardHeader
        title={<Typography variant="h6">Device & Client Breakdown</Typography>}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <TimeRangeToggle value={timeRange} onChange={setTimeRange} disabled={isLoading} />
            <Tooltip title="Refresh breakdown">
              <span>
                <IconButton
                  onClick={() => refetch()}
                  disabled={isFetching}
                  aria-label="Refresh device and client breakdown"
                >
                  {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
      />
      <CardContent>
        {error ? (
          <Alert severity="error">Unable to load device insights.</Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box sx={{ height: 240 }} role="img" aria-label="Email engagement by device type">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={deviceData}
                    dataKey="count"
                    nameKey="label"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                  >
                    {deviceData.map((entry, index) => (
                      <Cell key={entry.label} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value: number, _: string, item) => `${value} engagements (${item?.payload?.percentage ?? 0}%)`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <List dense aria-label="Email client breakdown">
              {clientData.map((client) => (
                <ListItem key={client.label} sx={{ px: 0 }}>
                  <ListItemText primary={client.label} />
                  <Chip
                    label={`${client.count} (${client.percentage}%)`}
                    size="small"
                    aria-label={`${client.label} accounted for ${client.percentage}% of engagements`}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default memo(DeviceClientBreakdown);
